import { BadgeOrigin, BadgeVariant, IconName } from "@axum/ax-design";
import { AxumOperationType, OrderStates } from "../types/orders/order";

const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });

export const formatMoney = (value: number | null | undefined) =>
  value === null || value === undefined ? "" : money.format(value);

const pad = (value: number) => String(value).padStart(2, "0");
const DAY_MS = 86400000;

export type DateAge = "today" | "week" | "month" | "old" | "stale";

export interface DateParts {
  day: string;
  time: string;
  relative: string;
  age: DateAge;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const relativeText = (date: Date, now: Date) => {
  const minutes = Math.round((now.getTime() - date.getTime()) / 60000);
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days <= 0) {
    if (minutes < 1) return "recién";
    if (minutes < 60) return `hace ${minutes} min`;
    return `hace ${Math.floor(minutes / 60)} h`;
  }
  if (days === 1) return "ayer";
  if (days < 7) return `hace ${days} días`;
  if (days < 31) return `hace ${Math.floor(days / 7)} sem.`;
  return `hace ${Math.floor(days / 30)} meses`;
};

const ageOf = (date: Date, now: Date): DateAge => {
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days <= 0) return "today";
  if (days < 7) return "week";
  if (days < 31) return "month";
  if (days < 180) return "old";
  return "stale";
};

export const orderDateParts = (value: string, now: Date = new Date()): DateParts | null => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return {
    day: `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
    relative: relativeText(date, now),
    age: ageOf(date, now),
  };
};

interface StateDisplay {
  label: string;
  variant: BadgeVariant;
  icon?: IconName;
  tone?: string;
}

const STATES: { [state: string]: StateDisplay } = {
  "nuevo": { label: "Nuevo", variant: "new", icon: "new" },
  "enviado": { label: "Enviado", variant: "processed", icon: "send" },
  "facturado": { label: "Facturado", variant: "processed", icon: "document" },
  "envio rechazado": { label: "Envío rechazado", variant: "noorder", icon: "error" },
  "error al enviar": { label: "Error al enviar", variant: "noorder", icon: "warning" },
  "reintentando envio": { label: "Reintentando envío", variant: "budget", icon: "repeat" },
  "cancelado": { label: "Cancelado", variant: "neutral", icon: "ban", tone: "cancelled" },
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

export const orderStateBadge = (erpState: string | null | undefined): StateDisplay => {
  const key = (erpState || "").toLowerCase();
  return STATES[key] || { label: capitalize(key), variant: "neutral" };
};

export interface OriginDisplay {
  label: string;
  tone: string;
  origin?: BadgeOrigin;
}

const ORIGINS: { [code: string]: OriginDisplay } = {
  MAS: { label: "MASUNO", tone: "masuno", origin: "masuno" },
  MASUNO: { label: "MASUNO", tone: "masuno", origin: "masuno" },
  TEL: { label: "Telesales", tone: "axtelesale", origin: "axtelesale" },
  AXUM: { label: "Axum", tone: "axum", origin: "axum" },
  BEES: { label: "BEES", tone: "bees" },
  MINEGOCIO: { label: "Mi Negocio", tone: "minegocio" },
  CHS: { label: "Chess", tone: "other" },
};

export const orderOriginBadge = (code: string | null | undefined): OriginDisplay =>
  ORIGINS[(code || "").toUpperCase()] || { label: code || "", tone: "other" };

export const isBudget = (order: any) => order.axumOperationTypeCode === AxumOperationType.PRESUPUESTO;

export const isRejectedOrder = (order: any) => {
  const state = (order.erpState || "").toLowerCase();
  return state === OrderStates.ENVIO_RECHAZADO || state === OrderStates.ERROR_AL_ENVIAR;
};

export const isDownloadPending = (order: any) =>
  (order.erpState || "").toLowerCase() === OrderStates.NUEVO && isBudget(order);
